import { useState, useRef, useEffect, type FormEvent } from 'react';
import type React from 'react';
import { Bot, Globe, GraduationCap, MessageSquare, PanelRight, Paperclip, Camera, FolderPlus, Folder, ChevronRight, Plus, ArrowUp, ArrowLeft, ArrowDown, Users, X, Square, RotateCcw } from 'lucide-react';
import { SkeletonMessage } from '../../core/ui/skeleton';
import { useToastContext } from '../../core/ui/toast/ToastProvider';
import SurfaceHeader from '../../core/layout/SurfaceHeader';
import MessageBubble from './MessageBubble';
import TypingDots from './TypingDots';
import type { Message, TabType, Professional } from '../../App';
import chiefyMascot from '../../assets/images/ChiefyMascot.png';
import { isChatQuotaExhausted } from '../../userService';
import ChatQuotaBanner from './ChatQuotaBanner';
import './ChatRoom.css';

/** Starter prompts shown on a new/empty chat so users aren't stuck at
 *  a blank page. Phrased in the product's voice (property development
 *  focus) and short enough to read at a glance. */
const STARTER_PROMPTS = [
    'Draft a feasibility summary for a 20-lot subdivision',
    'Explain the zoning rules I should check before buying in NSW',
    'Review this land plan and flag site constraints',
    'Help me structure a development-application cover letter',
] as const;

type ChatPartner = {
    name: string;
    avatar: string;
    online: boolean;
};

type GroupChatInfo = {
    name: string;
    memberCount: number;
    members: Professional[];
};

type ChatRoomProps = {
    messages: Message[];
    placeholder: string;
    title: string;
    subtitle?: string | undefined;
    isLoadingMessages?: boolean | undefined;
    activeTab: TabType;
    onMenuClick: () => void;
    showMenu: boolean;
    onTogglePanel?: (() => void) | undefined;
    isPanelOpen?: boolean | undefined;
    onBack?: (() => void) | undefined;
    chatPartner?: ChatPartner | undefined;
    groupChat?: GroupChatInfo | undefined;
    chatId?: string | undefined;
    projectId?: string | undefined;
    projects?: { id: string; name: string }[] | undefined;
    onMoveToProject?: ((projectId: string | null) => void) | undefined;
    onCreateProject?: (() => void) | undefined;
    onSendMessage?: ((content: string, file?: File | null) => Promise<void>) | undefined;
    userData?: import('../../userService').BackendUserWithPlan | null | undefined;
    /** Streaming state machine — used to gate Stop/Retry/Regenerate buttons. */
    chatState?: import('./useStreamingChat').ChatState | undefined;
    /** Aborts the current streaming request. No-op if nothing is in flight. */
    onStop?: (() => void) | undefined;
    /** Re-sends the last user turn. Used after cancel / error. */
    onRetry?: (() => Promise<void>) | undefined;
    /** Asks the server to regenerate the last assistant turn in place. */
    onRegenerate?: (() => Promise<void>) | undefined;
};

function ChatRoom({
    messages,
    placeholder,
    title,
    subtitle,
    isLoadingMessages = false,
    activeTab,
    onMenuClick,
    showMenu,
    onTogglePanel,
    isPanelOpen,
    onBack,
    chatPartner,
    groupChat,
    chatId,
    projectId,
    projects = [],
    onMoveToProject,
    onCreateProject,
    onSendMessage,
    userData,
    chatState,
    onStop,
    onRetry,
    onRegenerate,
}: ChatRoomProps) {
    const toast = useToastContext();
    const [input, setInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [messagesListEl, setMessagesListEl] = useState<HTMLDivElement | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesAreaRef = useRef<HTMLDivElement | null>(null);
    const [panelTooltip, setPanelTooltip] = useState<{ top: number; left: number } | null>(null);
    const [attachedFile, setAttachedFile] = useState<File | null>(null);
    const [showScrollBtn, setShowScrollBtn] = useState(false);
    const [showAttachMenu, setShowAttachMenu] = useState(false);
    const [showProjectSubmenu, setShowProjectSubmenu] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const attachMenuRef = useRef<HTMLDivElement>(null);

    const handleFileClick = () => {
        fileInputRef.current?.click();
    };

    const handleScreenshot = async () => {
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
            const track = stream.getVideoTracks()[0];
            if (!track) return;
            const canvas = document.createElement('canvas');
            const video = document.createElement('video');
            video.srcObject = stream;
            await video.play();
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            canvas.getContext('2d')?.drawImage(video, 0, 0);
            track.stop();
            canvas.toBlob((blob) => {
                if (blob) {
                    if (blob.size > 3_932_160) {
                        toast.error(`Screenshot is too large (${(blob.size / (1024 * 1024)).toFixed(1)} MB) — maximum is 3.9 MB. Try capturing a smaller area.`);
                        return;
                    }
                    const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
                    setAttachedFile(file);
                }
            }, 'image/png');
        } catch {
            // User cancelled or browser doesn't support
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (!file) {
                return;
            }
            
            if (file.size > 3_932_160) {
                toast.error(`File is too large. '${file.name}' is ${(file.size / (1024 * 1024)).toFixed(1)} MB — maximum is 3.9 MB.`);
                e.target.value = '';
                return;
            }
            setAttachedFile(file);
        }
    };

    const removeFile = () => {
        setAttachedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const showPanelTooltip = (e: React.MouseEvent) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setPanelTooltip({ top: rect.top + rect.height / 2, left: rect.left - 12 });
    };
    const hidePanelTooltip = () => setPanelTooltip(null);
    const handlePanelToggle = () => {
        hidePanelTooltip();
        onTogglePanel?.();
    };

    const hasStreamingAssistantMessage = messages.some(
        (m) => m.role === 'assistant' && m.streaming,
    );

    // When the user submits, there can be a short delay before the backend
    // emits the first streamed assistant message. Show a placeholder bubble
    // during that gap so the UI feels responsive.
    const showThinkingPlaceholder =
        isSubmitting && Boolean(onSendMessage) && !hasStreamingAssistantMessage;

    const quotaExhausted = isChatQuotaExhausted(userData);
    const isInputDisabled = isSubmitting || isLoadingMessages || quotaExhausted;

    // Check if user is near the bottom of the scrollable area
    const isNearBottom = (threshold = 100): boolean => {
        const scrollContainer = messagesAreaRef.current;
        if (!scrollContainer) return true; // Default to true if we can't check
        
        const distanceFromBottom = scrollContainer.scrollHeight - (scrollContainer.scrollTop + scrollContainer.clientHeight);
        return distanceFromBottom <= threshold;
    };

    const scrollToBottom = (behavior: ScrollBehavior = 'auto', force = false) => {
        if (!force && !isNearBottom(300)) {
            return;
        }
        const doScroll = () => {
            const el = messagesAreaRef.current;
            if (el) {
                el.scrollTo({ top: el.scrollHeight, behavior });
            }
        };
        requestAnimationFrame(doScroll);
        // Double-fire to catch late DOM updates (streaming)
        setTimeout(doScroll, 50);
    };

    // Keep chat pinned to bottom whenever content changes.
    // While streaming, `auto` (instant) beats `smooth` — each token would
    // otherwise queue another smooth-scroll animation that can't catch up
    // with the growing scrollHeight, and the user sees the text "run off".
    useEffect(() => {
        const hasStreaming = messages.some(m => m.streaming);
        const force = hasStreaming || showThinkingPlaceholder;
        const behavior: ScrollBehavior = hasStreaming ? 'auto' : 'smooth';
        scrollToBottom(behavior, force);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, showThinkingPlaceholder]);

    // When switching chats/projects or finishing load, jump to bottom (force).
    useEffect(() => {
        scrollToBottom('auto', true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId, isLoadingMessages]);

    // When the right panel is toggled, layout width changes; keep pinned to bottom (force).
    useEffect(() => {
        scrollToBottom('auto', true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPanelOpen]);

    // Close attach menu on click outside
    useEffect(() => {
        if (!showAttachMenu) return;
        const handler = (e: MouseEvent) => {
            if (attachMenuRef.current && !attachMenuRef.current.contains(e.target as Node)) {
                setShowAttachMenu(false);
                setShowProjectSubmenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showAttachMenu]);

    // Esc = Stop generating while a stream is in flight.
    useEffect(() => {
        if (!onStop) return;
        if (chatState !== 'streaming' && chatState !== 'submitting') return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onStop();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [chatState, onStop]);

    // Show/hide scroll-to-bottom button based on scroll position
    useEffect(() => {
        const el = messagesAreaRef.current;
        if (!el) return;
        const onScroll = () => setShowScrollBtn(!isNearBottom(200));
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messagesAreaRef.current]);

    // When the chat area resizes (e.g., right panel opens/closes with a transition),
    // keep pinned to bottom only if user is near bottom. Window resize won't fire for this.
    useEffect(() => {
        if (!messagesListEl) return;

        const resizeObserver = new ResizeObserver(() => {
            // Only auto-scroll on resize if user is near bottom
            scrollToBottom('auto', false);
        });

        resizeObserver.observe(messagesListEl);
        return () => resizeObserver.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messagesListEl]);

    // Also keep pinned on window resize, but only if near bottom.
    useEffect(() => {
        const handleResize = () => scrollToBottom('auto', false);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        const hasContent = input.trim() || attachedFile;
        if (!hasContent || isInputDisabled) return;

        const messageContent = input.trim();
        const fileToUpload = attachedFile;

        setInput('');
        setAttachedFile(null);
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setIsSubmitting(true);
        scrollToBottom('smooth', true);

        try {
            if (onSendMessage) {
                await onSendMessage(messageContent, fileToUpload);
            }
        } catch (error) {
            console.error('Failed to send message:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Get tab-specific icon
    const getTabIcon = () => {
        switch (activeTab) {
            case 'assistant': return <Bot size={18} />;
            case 'networking': return <Globe size={18} />;
            case 'education': return <GraduationCap size={18} />;
            default: return <MessageSquare size={18} />;
        }
    };

    // Check if this is a DM chat (has chatPartner) or group chat
    const isDmChat = !!chatPartner;
    const isGroupChat = !!groupChat;

    // Compose DM / group / default title in the title slot so the
    // avatar + name visual survives the chrome migration. Subtitle is
    // handled natively via SurfaceHeader's subtitle prop for DM/group
    // (muted second line matches the legacy `.header-subtitle`).
    const renderedIcon = !isDmChat && !isGroupChat ? getTabIcon() : undefined;
    const renderedTitle: React.ReactNode = isGroupChat && groupChat ? (
        <div className="chatroom-header-title chatroom-header-title--group">
            <span className="group-avatar-header"><Users size={16} /></span>
            <span className="chatroom-header-title__name">{title}</span>
        </div>
    ) : isDmChat && chatPartner ? (
        <div className="chatroom-header-title chatroom-header-title--dm">
            <span className={`dm-avatar ${chatPartner.online ? 'online' : ''}`}>
                {chatPartner.avatar}
                {chatPartner.online && <span className="status-indicator" />}
            </span>
            <span className="chatroom-header-title__name">{title}</span>
        </div>
    ) : (
        <span className="chatroom-header-title chatroom-header-title--default">
            <span className="chatroom-header-title__name">{title}</span>
            {projectId && (
                <span className="header-project-label">
                    <Folder size={12} />
                    {projects.find(p => p.id === projectId)?.name || 'Project'}
                </span>
            )}
        </span>
    );

    const panelToggleAction = onTogglePanel ? (
        <button
            type="button"
            className={`action-btn panel-toggle ${isPanelOpen ? 'active' : ''}`}
            onClick={handlePanelToggle}
            onMouseEnter={showPanelTooltip}
            onMouseLeave={hidePanelTooltip}
        >
            <PanelRight size={18} />
        </button>
    ) : null;

    return (
        <div className={`chatroom ${messages.length === 0 && !isLoadingMessages && !showThinkingPlaceholder ? 'chatroom--empty' : ''}`}>
            <SurfaceHeader
                title={renderedTitle}
                {...(renderedIcon ? { icon: renderedIcon } : {})}
                {...(subtitle && (isDmChat || isGroupChat) ? { subtitle } : {})}
                onMenuClick={onMenuClick}
                showMenu={showMenu}
                {...(onBack ? { onBack, backLabel: 'Go back' } : {})}
                actions={panelToggleAction}
                onHeaderClick={() => messagesAreaRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
            />

            {/* Messages Area */}
            <div className="messages-area" ref={messagesAreaRef} aria-live="polite" aria-relevant="additions">
                {isLoadingMessages ? (
                    <div className="messages-list" ref={setMessagesListEl}>
                        <SkeletonMessage align="right" />
                        <SkeletonMessage align="left" />
                        <SkeletonMessage align="right" />
                        <SkeletonMessage align="left" />
                        <div ref={messagesEndRef} />
                    </div>
                ) : messages.length === 0 && !showThinkingPlaceholder ? (
                    <div className="empty-state">
                        {activeTab === 'assistant' && (
                            <img src={chiefyMascot} alt="Chiefy Mascot" className="chiefy-mascot" />
                        )}
                        {activeTab === 'assistant' ? (
                            <>
                                <h2>Welcome — I'm Chiefy</h2>
                                <p>Ask me anything to get started.</p>
                            </>
                        ) : (
                            <>
                                <div className="empty-icon">{getTabIcon()}</div>
                                <h2>Start a conversation</h2>
                                <p>Type a message below to get started</p>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="messages-list" ref={setMessagesListEl}>
                        {messages.map((message, idx) => {
                            // Regenerate lives on the *last* assistant turn,
                            // regardless of whether it completed cleanly, was
                            // cancelled, or errored — the user always wants
                            // another take. Hidden only while a turn is in
                            // flight (otherwise the Stop button handles it)
                            // and only once there's at least some content to
                            // replace.
                            const isLastAssistant =
                                message.role === 'assistant' &&
                                !message.streaming &&
                                !messages.slice(idx + 1).some((m) => m.role === 'assistant');
                            const hasContent = !!message.content?.trim();
                            const inFlight =
                                chatState === 'streaming' || chatState === 'submitting';
                            const canRegenerate =
                                isLastAssistant && hasContent && !inFlight && !!onRegenerate;
                            return (
                                <MessageBubble
                                    key={message.id}
                                    message={message}
                                    onRegenerate={canRegenerate ? onRegenerate : undefined}
                                />
                            );
                        })}
                        {showThinkingPlaceholder && (
                            <div key="thinking" className="message assistant">
                                <div className="bubble assistant">
                                    <TypingDots />
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>

            {/* Scroll to bottom button */}
            {showScrollBtn && messages.length > 0 && (
                <button
                    type="button"
                    className="scroll-to-bottom-btn"
                    onClick={() => scrollToBottom('smooth', true)}
                    aria-label="Scroll to bottom"
                >
                    <ArrowDown size={18} />
                </button>
            )}

            {/* Welcome content — shown above input when empty. First-chat
             *  anchor: mascot + starter prompts so the user isn't staring
             *  at a blank page wondering what Chiefy can do. */}
            {messages.length === 0 && !isLoadingMessages && !showThinkingPlaceholder && activeTab === 'assistant' && (
                <div className="empty-welcome">
                    <img src={chiefyMascot} alt="Chiefy" className="chiefy-mascot" />
                    <h2 className="welcome-title">Welcome — I'm Chiefy</h2>
                    <p className="welcome-subtitle">Ask me anything to get started.</p>
                    <div className="welcome-starters" role="list">
                        {STARTER_PROMPTS.map((p) => (
                            <button
                                key={p}
                                type="button"
                                role="listitem"
                                className="welcome-starter"
                                onClick={() => {
                                    setInput(p);
                                    // Focus the composer so the user can
                                    // edit before sending, rather than
                                    // auto-submitting (respect agency).
                                    textareaRef.current?.focus();
                                }}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Retry — only after error/cancel. Stop is now the send-button
             *  icon swap (see below); Regenerate lives next to Copy on the
             *  last assistant bubble. */}
            {(chatState === 'error' || chatState === 'cancelled') && onRetry && (
                <div className="chat-turn-controls">
                    <button
                        type="button"
                        className="chat-turn-btn"
                        onClick={() => { void onRetry(); }}
                    >
                        <RotateCcw size={14} />
                        <span>{chatState === 'error' ? 'Retry' : 'Try again'}</span>
                    </button>
                </div>
            )}

            {/* Chat quota pre-warn — fires at 80% consumption (Phase G6)
             *  so the user sees the wall coming instead of hitting the
             *  paywall modal mid-conversation. */}
            <ChatQuotaBanner />

            {/* Input Area */}
            <form className="input-area" onSubmit={handleSubmit}>
                <div className="input-container">
                    {/* Chips: project badge + file attachment */}
                    {(projectId && !messages.length || attachedFile) && (
                        <div className="input-chips">
                            {projectId && !messages.length && (
                                <div className="input-chip input-chip--project">
                                    <Folder size={14} />
                                    <span>{projects.find(p => p.id === projectId)?.name || 'Project'}</span>
                                    <button type="button" className="input-chip-remove" onClick={() => onMoveToProject?.(null)} title="Remove from project">
                                        <X size={12} />
                                    </button>
                                </div>
                            )}
                            {attachedFile && (
                                <div className="input-chip input-chip--file">
                                    <Paperclip size={14} />
                                    <span>{attachedFile.name}</span>
                                    <button type="button" className="input-chip-remove" onClick={removeFile} title="Remove attachment">
                                        <X size={12} />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        aria-hidden="true"
                    />
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={(e) => {
                            setInput(e.target.value);
                            e.target.style.height = 'auto';
                            e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                if (input.trim() && !isInputDisabled) {
                                    (e.target as HTMLTextAreaElement).form?.requestSubmit();
                                }
                            }
                        }}
                        placeholder={placeholder}
                        disabled={isInputDisabled}
                        className="chat-input"
                        rows={1}
                    />
                    <div className="input-bottom-row">
                        <div className="input-actions-left">
                            <div className="attach-menu-container" ref={attachMenuRef}>
                                <button
                                    type="button"
                                    className={`input-action attach-toggle ${showAttachMenu ? 'active' : ''}`}
                                    title="Add content"
                                    onClick={() => setShowAttachMenu(!showAttachMenu)}
                                >
                                    <Plus size={18} />
                                </button>
                                {showAttachMenu && (
                                    <div className="attach-menu">
                                        {!showProjectSubmenu ? (
                                            <>
                                                <button type="button" className="attach-menu-item" onClick={() => { handleFileClick(); setShowAttachMenu(false); }}>
                                                    <Paperclip size={18} />
                                                    <span>Add files or photos</span>
                                                </button>
                                                {typeof navigator?.mediaDevices?.getDisplayMedia === 'function' && (
                                                    <button type="button" className="attach-menu-item" onClick={() => { handleScreenshot(); setShowAttachMenu(false); }}>
                                                        <Camera size={18} />
                                                        <span>Take a screenshot</span>
                                                    </button>
                                                )}
                                                <div className="attach-menu-divider" />
                                                <button type="button" className="attach-menu-item" onClick={() => setShowProjectSubmenu(true)}>
                                                    <FolderPlus size={18} />
                                                    <span>{projectId ? 'Move to project' : 'Add to project'}</span>
                                                    <ChevronRight size={14} className="attach-menu-chevron" />
                                                </button>
                                            </>
                                        ) : (
                                            <div className="attach-submenu">
                                                <button type="button" className="attach-menu-item attach-menu-back" onClick={() => setShowProjectSubmenu(false)}>
                                                    <ArrowLeft size={16} />
                                                    <span>Back</span>
                                                </button>
                                                <div className="attach-menu-divider" />
                                                {projectId && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="attach-menu-item"
                                                            onClick={() => { onMoveToProject?.(null); setShowAttachMenu(false); setShowProjectSubmenu(false); }}
                                                        >
                                                            <X size={16} />
                                                            <span>Remove from project</span>
                                                        </button>
                                                        <div className="attach-menu-divider" />
                                                    </>
                                                )}
                                                {projects.filter(p => p.id !== projectId).map(p => (
                                                    <button
                                                        key={p.id}
                                                        type="button"
                                                        className="attach-menu-item"
                                                        onClick={() => { onMoveToProject?.(p.id); setShowAttachMenu(false); setShowProjectSubmenu(false); }}
                                                    >
                                                        <Folder size={16} />
                                                        <span>{p.name}</span>
                                                    </button>
                                                ))}
                                                <div className="attach-menu-divider" />
                                                <button type="button" className="attach-menu-item attach-menu-create" onClick={() => { onCreateProject?.(); setShowAttachMenu(false); setShowProjectSubmenu(false); }}>
                                                    <Plus size={16} />
                                                    <span>Start new project</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        {chatState === 'streaming' || chatState === 'submitting' ? (
                            // Swap the send affordance for a Stop affordance while
                            // a turn is in flight — same pixel position, same
                            // enabled state, just a different semantic action.
                            // Must be type=button so it doesn't submit the form.
                            <button
                                type="button"
                                className="send-btn send-btn--stop"
                                onClick={() => onStop?.()}
                                disabled={!onStop}
                                aria-label="Stop generating response"
                                title="Stop generating"
                            >
                                <Square size={14} fill="currentColor" />
                            </button>
                        ) : (
                            <button
                                type="submit"
                                className="send-btn"
                                disabled={!input.trim() || isInputDisabled}
                                aria-label="Send message"
                                title="Send message"
                            >
                                <ArrowUp size={16} />
                            </button>
                        )}
                    </div>
                </div>
            </form>

            {/* Footer — Claude-style: centered disclosure below input */}
            {activeTab === 'assistant' && (
                <div className="chat-footer">
                    <p className="ai-disclosure">
                        Chiefy is AI and can make mistakes. Please double-check responses.
                    </p>
                </div>
            )}

            {/* Panel toggle tooltip */}
            {panelTooltip && (
                <div
                    className="panel-toggle-tooltip"
                    style={{ top: panelTooltip.top, left: panelTooltip.left }}
                >
                    {isPanelOpen ? 'Close panel' : 'Open panel'}
                </div>
            )}
        </div>
    );
}

export default ChatRoom;
